import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { Check } from 'lucide-react';
import { ONBOARDING_STEP_DEFS } from './onboardingDashboardConstants';

const PIPELINE_GROUP_INDEX = {
  onboardingSent: 0,
  onboardingSubmitted: 1,
  paSigned: 2,
  onboardingCompleted: 3
};

function getStepCompletion(record, stepKey) {
  if (stepKey === 'onboardingCompleted') {
    return Boolean(record?.onboardingComplete ?? record?.onboardingCompleted);
  }
  return Boolean(record?.[stepKey]);
}

function getDeniedStepStates(record) {
  const steps = ONBOARDING_STEP_DEFS.map(def =>
    getStepCompletion(record, def.key)
  );

  if (steps.some(Boolean)) {
    return steps.map(completed => (completed ? 'done' : 'denied'));
  }

  const lastStep = Number(record?.lastCompletedStep);
  if (Number.isFinite(lastStep) && lastStep > 0) {
    return ONBOARDING_STEP_DEFS.map((_, i) =>
      i < lastStep ? 'done' : 'denied'
    );
  }

  return ONBOARDING_STEP_DEFS.map(() => 'pending');
}

export function computeOnboardingStepStates(record) {
  const groupKey = record?.groupKey;

  if (groupKey === 'onboardingDenied') {
    return getDeniedStepStates(record);
  }

  if (groupKey === 'onboardingCompleted') {
    return ONBOARDING_STEP_DEFS.map(() => 'done');
  }

  if (groupKey && groupKey in PIPELINE_GROUP_INDEX) {
    const currentIndex = PIPELINE_GROUP_INDEX[groupKey];
    return ONBOARDING_STEP_DEFS.map((_, i) => {
      if (i < currentIndex) return 'done';
      if (i === currentIndex) return 'current';
      return 'pending';
    });
  }

  const steps = ONBOARDING_STEP_DEFS.map(def => getStepCompletion(record, def.key));

  if (!steps.some(Boolean)) {
    return steps.map(() => 'pending');
  }

  if (steps.every(Boolean)) {
    return steps.map(() => 'done');
  }

  const firstIncomplete = steps.findIndex(s => !s);
  return steps.map((done, i) => {
    if (done) return 'done';
    if (i === firstIncomplete) return 'current';
    return 'pending';
  });
}

function stepDotClass(state, sizeClass) {
  const base = `inline-flex shrink-0 items-center justify-center rounded-full border ${sizeClass}`;

  if (state === 'done') {
    return `${base} border-secondary-darkened bg-secondary-darkened text-white`;
  }
  if (state === 'current') {
    return `${base} border-primary-darkened bg-primary-darkened text-white`;
  }
  if (state === 'denied') {
    return `${base} border-semantic-danger bg-semantic-danger text-white`;
  }
  return `${base} border-grey-lighter bg-grey-lightest`;
}

function OnboardingProgressStepper({ record, dense = true }) {
  const states = useMemo(
    () => computeOnboardingStepStates(record ?? {}),
    [record]
  );

  const sizeClass = dense ? 'h-5 w-5' : 'h-6 w-6';

  return (
    <div
      className={`flex items-center gap-0.5 ${dense ? 'max-w-[128px]' : 'gap-2'}`}
      role='img'
      aria-label='Onboarding progress'
    >
      {ONBOARDING_STEP_DEFS.map((def, i) => {
        const state = states[i];
        const dot = (
          <span className={stepDotClass(state, sizeClass)}>
            {state === 'done' ? (
              <Check className='size-3 stroke-[3]' aria-hidden />
            ) : state === 'current' ? (
              <span className='h-1.5 w-1.5 rounded-full bg-white' />
            ) : state === 'denied' ? (
              <span className='h-1.5 w-1.5 rounded-full bg-white' />
            ) : null}
          </span>
        );

        return (
          <Tooltip key={def.key} title={def.label} arrow placement='top'>
            <span className='inline-flex cursor-default'>{dot}</span>
          </Tooltip>
        );
      })}
    </div>
  );
}

OnboardingProgressStepper.propTypes = {
  record: propTypes.object,
  dense: propTypes.bool
};

export default OnboardingProgressStepper;
